import { Search, StarBorderOutlined } from "@mui/icons-material"
import { Box } from "@mui/material"
import { useEffect, useMemo, useRef, useState } from "react"
import intl from "src/internationalization/intl"
import {
    AggregatedOpportunitiesChart,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    InnerSection,
    ObjectiveCard,
    ObjectiveMonitoringPanel,
    ObjectiveMonitorTrigger,
    Section,
    Select,
    TextInput,
    Typography,
} from "src/lib"
import { isManagerProfile } from "src/lib/utils/isManagerProfile"
import { getDueInDays } from "src/lib/utils/opportunityDates"
import type { IOpportunity } from "src/services/api/opportunities/types"
import { useUserStore } from "src/store/userStore"
import { useOpportunitiesFilters } from "./useOpportunitiesFilters"
import { useOpportunitiesSection } from "./useOpportunitiesSection"
import { useObjectiveEventsChannel } from "./useObjectiveEventsChannel"
import { getObjectiveListKey } from "./objectiveListKey"
import type { IOpportunitiesSectionProps } from "./types"

const OpportunitiesSection = ({
    showMonitoringPanel = false,
    showCreateObjectiveButton = true,
    showManageObjectiveActions = false,
}: IOpportunitiesSectionProps = {}) => {
    const { user } = useUserStore()
    const canCreateObjectives = isManagerProfile(user?.profile)
    const [selectedMonitoringKeys, setSelectedMonitoringKeys] = useState<string[]>([])
    const [search, setSearch] = useState("")
    const [searchOpen, setSearchOpen] = useState(false)
    const monitoringPanelRef = useRef<HTMLDivElement | null>(null)

    const {
        sortDirection,
        clientsImpactedFilter,
        statusFilter,
        sortOptions,
        statusOptions,
        clientsImpactedOptions,
        filters,
        handleSortChange,
        handleClientsImpactedChange,
        handleStatusChange,
    } = useOpportunitiesFilters()

    const {
        objectives,
        expiredObjectives,
        isLoading,
        error,
        showAll,
        handleOpenNewObjectiveModal,
        formatAddedAt,
        formatDueDate,
        toggleShowAll,
        handleClickAIButton,
    } = useOpportunitiesSection(filters)

    useObjectiveEventsChannel()

    const statusFilteredObjectives = useMemo(() => {
        if (statusFilter === "expired") return objectives.filter((o) => o.isExpired)
        if (statusFilter === "all") return objectives
        return objectives.filter((o) => !o.isExpired)
    }, [objectives, statusFilter])

    const filteredObjectives = useMemo(() => {
        const query = search.trim().toLowerCase()
        if (!query) return statusFilteredObjectives
        return statusFilteredObjectives.filter(
            (o) =>
                (o.title ?? "").toLowerCase().includes(query) ||
                (o.text ?? "").toLowerCase().includes(query),
        )
    }, [statusFilteredObjectives, search])

    const displayedObjectives = useMemo(
        () => (showAll ? filteredObjectives : filteredObjectives.slice(0, 4)),
        [filteredObjectives, showAll],
    )

    // Oportunidades selecionadas (preserva a ordem da listagem).
    const selectedObjectives = useMemo(
        () =>
            objectives.filter((o) => selectedMonitoringKeys.includes(getObjectiveListKey(o))),
        [selectedMonitoringKeys, objectives]
    )

    useEffect(() => {
        if (selectedMonitoringKeys.length === 0 || !monitoringPanelRef.current) return
        monitoringPanelRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }, [selectedMonitoringKeys])

    const toggleMonitoring = (listKey: string) => {
        setSelectedMonitoringKeys((prev) =>
            prev.includes(listKey) ? prev.filter((k) => k !== listKey) : [...prev, listKey]
        )
    }

    const renderObjectiveCard = (objective: IOpportunity) => {
        const listKey = getObjectiveListKey(objective)
        const monitoringActive = selectedMonitoringKeys.includes(listKey)

        return (
            <ObjectiveCard
                compact
                fullWidth
                fullHeight
                selected={monitoringActive}
                title={objective.title}
                objectiveId={objective.objective_id}
                recommendation={objective.text}
                addedAt={formatAddedAt(objective.createdDate)}
                dueDate={formatDueDate(objective)}
                dueDateIso={objective.dueDate}
                createdDate={objective.createdDate}
                endDate={objective.endDate}
                dueInDays={getDueInDays(objective)}
                isExpired={objective.isExpired}
                impactedClients={String(objective.impacted_clients?.length ?? 0)}
                impactedClientsArray={objective.impacted_clients}
                context={objective.title}
                showImpactedClients={true}
                showManageActions={showManageObjectiveActions}
                monitorButton={
                    showMonitoringPanel ? (
                        <ObjectiveMonitorTrigger
                            active={monitoringActive}
                            onClick={() => toggleMonitoring(listKey)}
                        />
                    ) : undefined
                }
            />
        )
    }

    return (
        <Section
            title={intl("opportunities")}
            icon={<StarBorderOutlined />}
            titleAdornment={
                searchOpen ? (
                    <Box sx={{ width: { xs: 160, sm: 240 } }}>
                        <TextInput
                            autoFocus
                            placeholder={intl("search")}
                            value={search}
                            onChange={(e: any) => setSearch(e.target.value)}
                            onBlur={() => {
                                if (!search.trim()) setSearchOpen(false)
                            }}
                            startAdornment={<Search />}
                            size="small"
                        />
                    </Box>
                ) : (
                    <IconButton
                        variant="clean"
                        color="neutral"
                        size="small"
                        aria-label={intl("search")}
                        onClick={() => setSearchOpen(true)}
                    >
                        <Search />
                    </IconButton>
                )
            }
            headerComponents={
                canCreateObjectives && showCreateObjectiveButton ? (
                    <Button variant="contained" onClick={handleOpenNewObjectiveModal}>
                        <Typography>{intl("addNewOpportunity")}</Typography>
                    </Button>
                ) : undefined
            }
            onClickAIButton={handleClickAIButton}
        >
            <Grid container justifyContent="flex-end" alignItems="center" gap={1.5} sx={{ mb: 1 }}>
                <Grid item>
                    <Select
                        value={statusFilter}
                        onChange={handleStatusChange}
                        options={statusOptions}
                        size="small"
                        aria-label={intl("statusFilterLabel")}
                    />
                </Grid>
                <Grid item>
                    <Select
                        value={sortDirection}
                        onChange={handleSortChange}
                        options={sortOptions}
                        size="small"
                    />
                </Grid>
                <Grid item>
                    <Select
                        value={clientsImpactedFilter}
                        onChange={handleClientsImpactedChange}
                        options={clientsImpactedOptions}
                        size="small"
                    />
                </Grid>
                {statusFilter === "expired" && expiredObjectives.length > 0 && (
                    <Grid item>
                        <Typography variant="caption" color="quaternary">
                            {`${expiredObjectives.length} ${intl("statusExpired").toLowerCase()}`}
                        </Typography>
                    </Grid>
                )}
            </Grid>
            <InnerSection title="" mt={1}>
                {isLoading && (
                    <Grid container justifyContent="center" sx={{ py: 4, width: "100%" }}>
                        <CircularProgress color="primary" />
                    </Grid>
                )}

                {error && !isLoading && (
                    <Typography variant="body2" color="secondary">
                        {error}
                    </Typography>
                )}

                {!isLoading && !error && filteredObjectives.length === 0 && (
                    <Typography variant="body2" color="quaternary" sx={{ py: 3, display: "block" }}>
                        Nenhuma oportunidade encontrada para os filtros atuais.
                    </Typography>
                )}

                {!isLoading && !error && filteredObjectives.length > 0 && (
                    <Box
                        sx={{
                            mt: 1,
                            display: "grid",
                            // colunas refluem pela largura real (adaptam ao abrir o assistente)
                            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                            gap: 1.5,
                            alignItems: "stretch",
                        }}
                    >
                        {displayedObjectives.map((objective) => (
                            <Box key={getObjectiveListKey(objective)} sx={{ minWidth: 0 }}>
                                {renderObjectiveCard(objective)}
                            </Box>
                        ))}
                    </Box>
                )}

                {showMonitoringPanel && selectedObjectives.length === 1 && (
                    <Box ref={monitoringPanelRef} sx={{ mt: 3 }}>
                        <ObjectiveMonitoringPanel
                            variant="standalone"
                            objectiveTitle={selectedObjectives[0].title}
                            onClose={() => setSelectedMonitoringKeys([])}
                            movementPortfolioRollups={selectedObjectives[0].movement_portfolio_rollups}
                            movementSummary={selectedObjectives[0].summary}
                            objectiveWarnings={selectedObjectives[0].warnings}
                            impactedClients={selectedObjectives[0].impacted_clients}
                            dailyExecutionSeries={selectedObjectives[0].daily_execution_series}
                            createdDate={selectedObjectives[0].createdDate}
                            dueDate={selectedObjectives[0].dueDate}
                        />
                    </Box>
                )}

                {showMonitoringPanel && selectedObjectives.length > 1 && (
                    <Box ref={monitoringPanelRef} sx={{ mt: 3 }}>
                        <AggregatedOpportunitiesChart
                            objectives={selectedObjectives}
                            onClose={() => setSelectedMonitoringKeys([])}
                        />
                    </Box>
                )}
            </InnerSection>

            {filteredObjectives.length > 4 && (
                <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
                    <Button variant="outlined" color="neutral" onClick={toggleShowAll}>
                        {showAll ? intl("showLessOpportunities") : intl("showMoreOpportunities")}
                    </Button>
                </Grid>
            )}
        </Section>
    )
}

export { OpportunitiesSection }
